/**
 * @description Live-day pace, decision line, scheduled exclusion, and
 *   volume grains for TowTech Scope 1 (LYN-400 / LYN-402 / LYN-438 / LYN-445).
 *
 * @dependencies ./scope1Dashboard
 *
 * @notes jobs stays SoR. Completed is the volume clock (LYN-455). Current
 *   Activity is the one En Route card. Band width is the company's own
 *   spread (IQR of comparison points), not a fixed job count. Scheduled
 *   rows stay in volume and leave response / late when etaAt is set or
 *   received-to-scene is past the locked cap. Month volume is one point
 *   per civil day; quarter and year are one point per month. Do not add
 *   a writer.
 */

import {
  USUAL_WEEKS,
  VOLUME_STAMP,
  jobStampAt,
  RESPONSE_ON_SCENE,
  addZonedDays,
  addZonedMonths,
  chartTimeZone,
  countByJobField,
  formatSeconds,
  formatVolume,
  isoWeekdayInTimeZone,
  windowForFilter,
  jobDurationSeconds,
  lateRateByType,
  parseMinCallCount,
  topCount,
  weekdayName,
  zonedCivilToUtc,
  zonedDateParts,
  zonedDaySpan,
  zonedDayStart,
  zonedMonthStart,
  zonedPeriodEnd,
  zonedQuarterStart,
  zonedWeekStart,
  zonedYearStart
} from './scope1Dashboard';
import { resolveDriverIdentity } from './scope1Identity';

export const MIN_HIGHLIGHT_TOWS = 8;
export const SCHEDULED_RESPONSE_CAP_SECONDS = 4 * 60 * 60;
export const LATE_ARRIVAL_TOOLTIP = 'Late among tows with a recorded arrival.';
export const ON_PACE_TITLE = 'On pace';
export const NEEDS_ATTENTION_TITLE = 'Needs attention';
export const ON_PACE_SENTENCE = 'Volume, response time, drivers and vehicle mix are on pace.';
export const ON_PACE_DECISION = ON_PACE_SENTENCE;
export const MIX_WEIGHT_CLASSES = Object.freeze(['Heavy', 'Medium', 'Light']);

/**
 * @param {Array<number>} values
 * @returns {number|null}
 */
export function median(values) {
  const sorted = (values || []).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Per-company band: median of the comparison points. Width is the
 * interquartile range of those points (the company's own spread).
 *
 * @param {Array<number>} points
 * @returns {{ usual: number|null, low: number|null, high: number|null, width: number, spread: number }}
 */
export function paceBand(points) {
  const values = (points || []).filter((value) => Number.isFinite(value));
  const usual = median(values);
  if (usual == null) {
    return { usual: null, low: null, high: null, width: 0, spread: 0 };
  }
  const spread = interquartileRange(values);
  return { usual, low: usual - spread, high: usual + spread, width: spread, spread };
}

/**
 * @param {Array<number>} values
 * @returns {number}
 */
export function interquartileRange(values) {
  const sorted = (values || []).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return 0;
  const q1 = percentile(sorted, 25);
  const q3 = percentile(sorted, 75);
  if (q1 == null || q3 == null) return 0;
  return Math.max(0, q3 - q1);
}

/**
 * How far a value sits outside the company's own band, in that band's
 * spread. Zero when inside or when there is no spread.
 *
 * @param {number|null|undefined} current
 * @param {{ usual?: number|null, low?: number|null, high?: number|null, spread?: number, width?: number }} band
 * @returns {number}
 */
export function distanceOutsideBand(current, band) {
  if (current == null || !Number.isFinite(Number(current)) || band?.usual == null) return 0;
  const value = Number(current);
  const spread = Number(band.spread ?? band.width) || 0;
  if (value >= band.low && value <= band.high) return 0;
  const gap = value < band.low ? band.low - value : value - band.high;
  if (spread <= 0) return gap > 0 ? Number.POSITIVE_INFINITY : 0;
  return gap / spread;
}

/**
 * @param {number|null|undefined} current
 * @param {{ low?: number|null, high?: number|null }} band
 * @returns {'behind'|'on'|'ahead'|'unknown'}
 */
export function pacePosition(current, band) {
  if (current == null || !Number.isFinite(Number(current)) || band?.usual == null) {
    return 'unknown';
  }
  const value = Number(current);
  if (value < band.low) return 'behind';
  if (value > band.high) return 'ahead';
  return 'on';
}

/**
 * @param {Date|string} value
 * @param {string} [timeZone]
 * @param {{ withMinutes?: boolean }} [opts]
 * @returns {string}
 */
export function formatClockTime(value, timeZone, { withMinutes = false } = {}) {
  const parts = zonedDateParts(value, timeZone);
  const suffix = parts.hour < 12 ? 'am' : 'pm';
  const hour = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
  if (!withMinutes) return `${hour}${suffix}`;
  return `${hour}:${String(parts.minute).padStart(2, '0')}${suffix}`;
}

/**
 * @param {object} job
 * @returns {Date|null}
 */
/**
 * Volume-stamp (en route) instant. Pace uses the same stamp as the Tows card.
 *
 * @param {object} job
 * @returns {Date|null}
 */
export function jobVolumeAt(job, stamp = VOLUME_STAMP) {
  return jobStampAt(job, stamp);
}

/**
 * Same elapsed time into the prior window as now is into the current window.
 * Hour/day/week current windows end at period close; the prior is fully elapsed.
 *
 * @param {{ from?: Date|string }} currentWindow
 * @param {{ from?: Date|string }} previousWindow
 * @param {Date|string} now
 * @returns {Date|null}
 */
export function elapsedPriorCutoff(currentWindow, previousWindow, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  const currentFrom = currentWindow?.from != null ? new Date(currentWindow.from) : null;
  const previousFrom = previousWindow?.from != null ? new Date(previousWindow.from) : null;
  const when = now != null ? new Date(now) : null;
  if (!currentFrom || Number.isNaN(currentFrom.getTime())
    || !previousFrom || Number.isNaN(previousFrom.getTime())
    || !when || Number.isNaN(when.getTime())) {
    return null;
  }
  let dayDelta = 0;
  let cursor = zonedDayStart(currentFrom, tz);
  const nowDay = zonedDayStart(when, tz);
  while (cursor < nowDay) {
    dayDelta += 1;
    cursor = addZonedDays(cursor, 1, tz);
    if (dayDelta > 400) break;
  }
  const priorDay = addZonedDays(zonedDayStart(previousFrom, tz), dayDelta, tz);
  return sameClockOnDay(priorDay, when, tz);
}

/**
 * Prior jobs whose volume stamp is before the elapsed cutoff.
 *
 * @param {Array<object>} jobs
 * @param {{ from?: Date|string }} currentWindow
 * @param {{ from?: Date|string }} previousWindow
 * @param {Date|string} now
 * @returns {Array<object>}
 */
export function jobsInElapsedPrior(jobs, currentWindow, previousWindow, now, timeZone, stamp = VOLUME_STAMP) {
  const cutoff = elapsedPriorCutoff(currentWindow, previousWindow, now, timeZone);
  if (!cutoff) return [];
  const end = cutoff.getTime();
  return (jobs || []).filter((job) => {
    const at = jobVolumeAt(job, stamp);
    return at && at.getTime() < end;
  });
}

/**
 * Volume-stamp tows in [from, cutoff). En route everywhere.
 *
 * @param {Array<object>} jobs
 * @param {Date} from
 * @param {Date} cutoff
 * @returns {Array<object>}
 */
export function volumeJobsThrough(jobs, from, cutoff, stamp = VOLUME_STAMP) {
  if (!from || !cutoff) return [];
  const start = from.getTime();
  const end = cutoff.getTime();
  return (jobs || []).filter((job) => {
    const at = jobVolumeAt(job, stamp);
    if (!at) return false;
    const ms = at.getTime();
    return ms >= start && ms < end;
  });
}

/**
 * @param {Date|string} value
 * @param {string} [timeZone]
 * @returns {boolean}
 */
export function isLiveDay(value, timeZone) {
  const tz = chartTimeZone(timeZone);
  const now = value instanceof Date ? value : new Date(value);
  return now.getTime() < zonedPeriodEnd('day', now, tz).getTime();
}

/**
 * Same wall-clock cutoff on another civil day.
 *
 * @param {Date} dayStart
 * @param {Date} now
 * @param {string} timeZone
 * @returns {Date}
 */
export function sameClockOnDay(dayStart, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  const day = zonedDateParts(dayStart, tz);
  const clock = zonedDateParts(now, tz);
  return zonedCivilToUtc({
    year: day.year,
    month: day.month,
    day: day.day,
    hour: clock.hour,
    minute: clock.minute,
    second: clock.second
  }, tz);
}

/**
 * Last 12 same weekdays: en route through this hour on each.
 *
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} [timeZone]
 * @param {number} [weeks]
 * @returns {number[]}
 */
export function dayPacePoints(jobs, now, timeZone, weeks = USUAL_WEEKS, stamp = VOLUME_STAMP) {
  const tz = chartTimeZone(timeZone);
  const today = zonedDayStart(now, tz);
  const points = [];
  for (let week = 1; week <= weeks; week += 1) {
    const start = addZonedDays(today, -week * 7, tz);
    points.push(volumeJobsThrough(jobs, start, sameClockOnDay(start, now, tz), stamp).length);
  }
  return points;
}

/**
 * Last 12 weeks: en route through this weekday (and this clock).
 *
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} [timeZone]
 * @param {number} [weeks]
 * @returns {number[]}
 */
export function weekPacePoints(jobs, now, timeZone, weeks = USUAL_WEEKS, stamp = VOLUME_STAMP) {
  const tz = chartTimeZone(timeZone);
  const thisWeek = zonedWeekStart(now, tz);
  const weekday = isoWeekdayInTimeZone(now, tz);
  const points = [];
  for (let week = 1; week <= weeks; week += 1) {
    const start = addZonedDays(thisWeek, -week * 7, tz);
    const cutoffDay = addZonedDays(start, weekday - 1, tz);
    points.push(volumeJobsThrough(jobs, start, sameClockOnDay(cutoffDay, now, tz), stamp).length);
  }
  return points;
}

/**
 * Last 12 months: completed through this date (and this clock).
 *
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} [timeZone]
 * @param {number} [months]
 * @returns {number[]}
 */
export function monthPacePoints(jobs, now, timeZone, months = 12, stamp = VOLUME_STAMP) {
  const tz = chartTimeZone(timeZone);
  const thisMonth = zonedMonthStart(now, tz);
  const clock = zonedDateParts(now, tz);
  const points = [];
  for (let month = 1; month <= months; month += 1) {
    const start = addZonedMonths(thisMonth, -month, tz);
    const startParts = zonedDateParts(start, tz);
    const dim = zonedDaySpan(start, addZonedMonths(start, 1, tz), tz);
    const day = Math.min(clock.day, dim);
    const cutoff = zonedCivilToUtc({
      year: startParts.year,
      month: startParts.month,
      day,
      hour: clock.hour,
      minute: clock.minute,
      second: clock.second
    }, tz);
    points.push(volumeJobsThrough(jobs, start, cutoff, stamp).length);
  }
  return points;
}

/**
 * Last 12 quarters: completed through this day-of-quarter (and this clock).
 *
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} [timeZone]
 * @param {number} [quarters]
 * @returns {number[]}
 */
export function quarterPacePoints(jobs, now, timeZone, quarters = 12, stamp = VOLUME_STAMP) {
  const tz = chartTimeZone(timeZone);
  const thisQuarter = zonedQuarterStart(now, tz);
  const clock = zonedDateParts(now, tz);
  const monthOffset = clock.month - zonedDateParts(thisQuarter, tz).month;
  const points = [];
  for (let quarter = 1; quarter <= quarters; quarter += 1) {
    const start = addZonedMonths(thisQuarter, -quarter * 3, tz);
    const cutoffMonthStart = addZonedMonths(start, monthOffset, tz);
    const cutoffParts = zonedDateParts(cutoffMonthStart, tz);
    const dim = zonedDaySpan(cutoffMonthStart, addZonedMonths(cutoffMonthStart, 1, tz), tz);
    const cutoff = zonedCivilToUtc({
      year: cutoffParts.year,
      month: cutoffParts.month,
      day: Math.min(clock.day, dim),
      hour: clock.hour,
      minute: clock.minute,
      second: clock.second
    }, tz);
    points.push(volumeJobsThrough(jobs, start, cutoff, stamp).length);
  }
  return points;
}

/**
 * Last 12 years: completed through this date (and this clock).
 *
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} [timeZone]
 * @param {number} [years]
 * @returns {number[]}
 */
export function yearPacePoints(jobs, now, timeZone, years = 12, stamp = VOLUME_STAMP) {
  const tz = chartTimeZone(timeZone);
  const thisYear = zonedYearStart(now, tz);
  const clock = zonedDateParts(now, tz);
  const points = [];
  for (let year = 1; year <= years; year += 1) {
    const start = addZonedMonths(thisYear, -year * 12, tz);
    const startParts = zonedDateParts(start, tz);
    const cutoffMonthStart = zonedCivilToUtc({
      year: startParts.year,
      month: clock.month,
      day: 1,
      hour: 0
    }, tz);
    const dim = zonedDaySpan(cutoffMonthStart, addZonedMonths(cutoffMonthStart, 1, tz), tz);
    const cutoff = zonedCivilToUtc({
      year: startParts.year,
      month: clock.month,
      day: Math.min(clock.day, dim),
      hour: clock.hour,
      minute: clock.minute,
      second: clock.second
    }, tz);
    points.push(volumeJobsThrough(jobs, start, cutoff, stamp).length);
  }
  return points;
}

/**
 * @param {string} filter
 * @param {Date} now
 * @param {string} [timeZone]
 * @returns {Date}
 */
export function pacePeriodStart(filter, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  if (filter === 'week') return zonedWeekStart(now, tz);
  if (filter === 'month') return zonedMonthStart(now, tz);
  if (filter === 'quarter') return zonedQuarterStart(now, tz);
  if (filter === 'year') return zonedYearStart(now, tz);
  return zonedDayStart(now, tz);
}

/**
 * @param {object} input
 * @returns {object}
 */
export function buildPace({
  jobs,
  filter = 'day',
  now,
  timeZone,
  weeks = USUAL_WEEKS,
  stamp = VOLUME_STAMP
} = {}) {
  const tz = chartTimeZone(timeZone);
  const when = now instanceof Date ? now : new Date(now);
  const live = isLiveDay(when, tz);
  const periodStart = pacePeriodStart(filter, when, tz);
  const current = volumeJobsThrough(jobs, periodStart, when, stamp).length;
  let points;
  if (filter === 'week') points = weekPacePoints(jobs, when, tz, weeks, stamp);
  else if (filter === 'month') points = monthPacePoints(jobs, when, tz, 12, stamp);
  else if (filter === 'quarter') points = quarterPacePoints(jobs, when, tz, 12, stamp);
  else if (filter === 'year') points = yearPacePoints(jobs, when, tz, 12, stamp);
  else points = dayPacePoints(jobs, when, tz, weeks, stamp);
  const band = paceBand(points);
  const position = pacePosition(current, band);
  let throughLabel = null;
  if (live) {
    if (filter === 'week') throughLabel = weekdayName(isoWeekdayInTimeZone(when, tz));
    else if (filter === 'month' || filter === 'quarter' || filter === 'year') {
      throughLabel = String(zonedDateParts(when, tz).day);
    } else {
      throughLabel = formatClockTime(when, tz);
    }
  }
  return {
    current,
    usual: band.usual,
    band,
    position,
    throughLabel,
    live,
    weekday: isoWeekdayInTimeZone(when, tz),
    asOf: formatClockTime(when, tz, { withMinutes: true }),
    points
  };
}

/**
 * "14 tows through 10am. A usual Thursday has 11 by now. Data as of 10:05am."
 *
 * @param {object} pace
 * @returns {string|null}
 */
export function paceLine(pace) {
  if (!pace || pace.current == null || pace.usual == null) return null;
  const day = weekdayName(pace.weekday);
  const current = formatVolume(pace.current);
  const usual = formatVolume(Math.round(Number(pace.usual)));
  const through = pace.live && pace.throughLabel ? ` through ${pace.throughLabel}` : '';
  return `${current} tows${through}. A usual ${day} has ${usual} by now. Data as of ${pace.asOf}.`;
}

/**
 * Current Activity copy. Data as of on every pace line.
 *
 * @param {object} pace
 * @returns {string|null}
 */
export function currentActivityLine(pace) {
  if (!pace || pace.current == null) return null;
  const current = formatVolume(pace.current);
  const through = pace.live && pace.throughLabel ? ` through ${pace.throughLabel}` : '';
  const usual = pace.usual == null ? '' : ` Usual by now is ${formatVolume(Math.round(Number(pace.usual)))}.`;
  return `${current} went en route${through}.${usual} Data as of ${pace.asOf}.`;
}

/**
 * @param {object} job
 * @param {number} [capSeconds]
 * @returns {boolean}
 */
export function isScheduledResponseJob(job, capSeconds = SCHEDULED_RESPONSE_CAP_SECONDS) {
  if (job?.etaAt) return true;
  const received = Date.parse(job?.createdAt || '');
  const scene = Date.parse(job?.onSceneAt || '');
  if (!Number.isFinite(received) || !Number.isFinite(scene) || scene < received) {
    return false;
  }
  return (scene - received) / 1000 > capSeconds;
}

/**
 * Scheduled stay in volume. They leave response and late.
 *
 * @param {Array<object>} jobs
 * @param {number} [capSeconds]
 * @returns {{ jobs: Array<object>, excludedCount: number }}
 */
export function jobsForResponse(jobs, capSeconds = SCHEDULED_RESPONSE_CAP_SECONDS) {
  const kept = [];
  let excludedCount = 0;
  for (const job of jobs || []) {
    if (isScheduledResponseJob(job, capSeconds)) {
      excludedCount += 1;
    } else {
      kept.push(job);
    }
  }
  return { jobs: kept, excludedCount };
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
export function normalizeClass(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (lower === 'light') return 'Light';
  if (lower === 'medium') return 'Medium';
  if (lower === 'heavy') return 'Heavy';
  return raw;
}

/**
 * A repeated class cell is one class.
 *
 * @param {Array<object>} jobs
 * @returns {Map<string, number>}
 */
export function countByClass(jobs) {
  const counts = new Map();
  for (const job of jobs || []) {
    const key = normalizeClass(job?.class);
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return counts;
}

/**
 * @param {string} key
 * @param {Map<string, number>} counts
 * @returns {{ count: number, total: number, share: number }}
 */
export function shareOfCounts(key, counts) {
  let total = 0;
  for (const count of (counts || []).values()) total += count;
  const count = counts?.get(key) || 0;
  return { count, total, share: total === 0 ? 0 : count / total };
}

/**
 * Quiet unless a class is outside the band. Light is included. A light-only
 * yard does not get a Light-only tile.
 *
 * @param {Array<object>} todayJobs
 * @param {Array<object>} usualJobs
 * @returns {object|null}
 */
export function mixOffFact(todayJobs, usualJobs, timeZone, stamp = VOLUME_STAMP) {
  const today = countByClass(todayJobs);
  const usual = countByClass(usualJobs);
  const present = new Set([...today.keys(), ...usual.keys()]);
  if (present.size === 0) return null;
  if (present.size === 1 && present.has('Light')) return null;
  let usualTotal = 0;
  for (const count of usual.values()) usualTotal += count;
  if (usualTotal === 0) return null;
  const weeklyShares = classSharesByWeek(usualJobs, timeZone, stamp);
  let worst = null;
  for (const key of MIX_WEIGHT_CLASSES) {
    if (!present.has(key)) continue;
    const current = shareOfCounts(key, today);
    const baseline = shareOfCounts(key, usual);
    if (baseline.total === 0) continue;
    const todayPct = current.share * 100;
    const usualPct = baseline.share * 100;
    const points = weeklyShares.get(key) || [usualPct];
    const band = paceBand(points);
    if (band.usual == null) continue;
    if (pacePosition(todayPct, band) === 'on' || pacePosition(todayPct, band) === 'unknown') {
      continue;
    }
    const distance = distanceOutsideBand(todayPct, band);
    if (distance <= 0) continue;
    if (!worst || distance > worst.distance) {
      worst = {
        key,
        todayPct,
        usualPct,
        count: current.count,
        total: current.total,
        delta: Math.abs(todayPct - usualPct),
        distance,
        sentence: `${key} is ${current.count} of ${current.total}, ${Math.round(todayPct)}%. Usual is ${Math.round(usualPct)}%.`
      };
    }
  }
  return worst;
}

/**
 * @param {Array<object>} jobs
 * @param {string} [timeZone]
 * @returns {Map<string, number[]>}
 */
function classSharesByWeek(jobs, timeZone, stamp = VOLUME_STAMP) {
  const tz = chartTimeZone(timeZone);
  const byWeek = new Map();
  for (const job of jobs || []) {
    const at = jobVolumeAt(job, stamp);
    if (!at) continue;
    const key = zonedWeekStart(at, tz).toISOString();
    if (!byWeek.has(key)) byWeek.set(key, []);
    byWeek.get(key).push(job);
  }
  const shares = new Map();
  for (const weekJobs of byWeek.values()) {
    const counts = countByClass(weekJobs);
    for (const key of MIX_WEIGHT_CLASSES) {
      const row = shareOfCounts(key, counts);
      if (!shares.has(key)) shares.set(key, []);
      shares.get(key).push(row.share * 100);
    }
  }
  return shares;
}

/**
 * @param {Array<number>} values
 * @param {number} p
 * @returns {number|null}
 */
export function percentile(values, p) {
  const sorted = (values || []).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  const rank = Math.min(sorted.length - 1, Math.max(0, Math.ceil((Number(p) / 100) * sorted.length) - 1));
  return sorted[rank];
}

/**
 * @param {unknown} seconds
 * @returns {string}
 */
export function formatMinutesShort(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return '—';
  return `${Math.round(Number(seconds) / 60)}m`;
}

/**
 * Volume callout without the Current Activity "Data as of" clock.
 *
 * @param {object} pace
 * @returns {string|null}
 */
export function volumeAttentionLine(pace) {
  if (!pace || pace.current == null || pace.usual == null) return null;
  const day = weekdayName(pace.weekday);
  const current = formatVolume(pace.current);
  const usual = formatVolume(Math.round(Number(pace.usual)));
  const through = pace.live && pace.throughLabel ? ` through ${pace.throughLabel}` : '';
  return `${current} tows${through}. A usual ${day} has ${usual} by now.`;
}

/**
 * Driver trending well below usual. Usual floor is the Overview minimum
 * (default 1), cut at the current clock the same way volume pace is. Says
 * the count, not underperforming. We do not have shifts.
 *
 * @param {object} input
 * @returns {object|null}
 */
export function driverLowFact({
  jobs,
  now,
  timeZone,
  weeks = USUAL_WEEKS,
  minCalls,
  filter = 'day',
  employees,
  labelCompany = false
} = {}) {
  if (!now) return null;
  if (filter === 'hour') return null;
  const tz = chartTimeZone(timeZone);
  const when = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(when.getTime())) return null;
  const denom = Number(weeks) > 0 ? Number(weeks) : USUAL_WEEKS;
  const floor = parseMinCallCount(minCalls);
  const currentWindow = filter === 'day' || filter === 'week' || filter === 'month'
    || filter === 'quarter' || filter === 'year'
    ? windowForFilter(filter, when, tz)
    : { from: zonedDayStart(when, tz), to: when };
  const currentEnd = currentWindow.to.getTime() > when.getTime()
    ? when
    : currentWindow.to;
  const labeled = (jobs || []).map((job) => {
    const identity = resolveDriverIdentity({
      rawName: job?.driver,
      companyName: labelCompany ? job?.companyName : '',
      companyId: job?.companyId,
      employees
    });
    return { ...job, _driverKey: identity.key, _driverLabel: identity.displayName };
  });
  const todayCounts = countByJobField(
    volumeJobsThrough(labeled, currentWindow.from, currentEnd),
    '_driverKey'
  );
  const weekCounts = [];
  const names = new Set(todayCounts.keys());
  const labels = new Map(labeled.map((job) => [job._driverKey, job._driverLabel]));
  const spanMs = currentEnd.getTime() - currentWindow.from.getTime();
  for (let week = 1; week <= denom; week += 1) {
    const start = new Date(currentWindow.from.getTime() - week * 7 * 24 * 60 * 60 * 1000);
    const end = new Date(start.getTime() + spanMs);
    const counts = countByJobField(
      volumeJobsThrough(labeled, start, end),
      '_driverKey'
    );
    weekCounts.push(counts);
    for (const name of counts.keys()) names.add(name);
  }
  let worst = null;
  for (const name of names) {
    const points = weekCounts.map((counts) => counts.get(name) || 0);
    const band = paceBand(points);
    if (band.usual == null || band.usual < floor) continue;
    const today = todayCounts.get(name) || 0;
    if (today < floor && today < 1) continue;
    if (pacePosition(today, band) !== 'behind') continue;
    const distance = distanceOutsideBand(today, band);
    if (distance <= 0) continue;
    if (!worst || distance > worst.distance) {
      const label = labels.get(name) || name;
      worst = {
        name: label,
        today,
        usual: band.usual,
        gap: band.usual - today,
        distance,
        sentence: `${label} is unusually low at ${formatVolume(today)} tows. Usual is ${formatVolume(Math.round(band.usual))}.`
      };
    }
  }
  return worst;
}

/**
 * @param {object} input
 * @returns {{ occupied?: boolean, title?: string, sentence?: string, name?: string }|null}
 */
function lateAlarm({ lateRate, usualLateRate, latePosition, lateCount, lateSample } = {}) {
  const shownLate = lateRate == null ? null : Math.round(Number(lateRate) * 100);
  const shownUsual = usualLateRate == null ? null : Math.round(Number(usualLateRate) * 100);
  const lateWorse = shownLate != null
    && shownUsual != null
    && latePosition !== 'on'
    && latePosition !== 'unknown'
    && shownLate > shownUsual;
  if (!lateWorse) return null;
  const counts = lateCount != null && lateSample != null
    ? ` on ${lateCount} of ${lateSample} arrivals`
    : '';
  const band = paceBand([Number(usualLateRate) * 100]);
  return {
    type: 'late',
    distance: distanceOutsideBand(Number(lateRate) * 100, {
      ...band,
      low: Number(usualLateRate) * 100,
      high: Number(usualLateRate) * 100
    }),
    sentence: `${shownLate}% are late${counts}, against ${shownUsual}%.`
  };
}

/**
 * Analysis alarms in band order: late, volume behind, driver trending low, mix.
 * Driver and mix wait until volume position is known. Occupied highlight
 * facts and names are left out. The card still falls back to the first raw
 * alarm when every visible one is already in Today's Highlights.
 *
 * @param {object} input
 * @returns {Array<{ type: string, sentence: string, name?: string }>}
 */
export function attentionAlarms({
  lateRate,
  usualLateRate,
  latePosition,
  lateCount,
  lateSample,
  volumePosition,
  mixOff,
  pace,
  driverLow,
  occupiedFacts = [],
  occupiedNames = []
} = {}) {
  const alarms = [];
  const late = lateAlarm({ lateRate, usualLateRate, latePosition, lateCount, lateSample });
  if (late) alarms.push(late);
  if (volumePosition === 'behind') {
    const sentence = volumeAttentionLine(pace);
    if (sentence) {
      alarms.push({
        type: 'volume',
        distance: distanceOutsideBand(pace?.current, pace?.band),
        sentence
      });
    }
  }
  const volumeKnown = volumePosition !== 'unknown' && volumePosition != null;
  if (volumeKnown) {
    if (driverLow?.sentence) {
      alarms.push({
        type: 'driverLow',
        name: driverLow.name,
        distance: Number(driverLow.distance) || 0,
        sentence: driverLow.sentence
      });
    }
    if (mixOff?.sentence) {
      alarms.push({
        type: 'classMix',
        distance: Number(mixOff.distance) || 0,
        sentence: mixOff.sentence
      });
    }
  }
  const types = new Set(occupiedFacts);
  const names = new Set(occupiedNames);
  return alarms.filter((alarm) => {
    if (alarm.type && types.has(alarm.type)) return false;
    if (alarm.name && names.has(alarm.name)) return false;
    return true;
  });
}

/**
 * Late, then volume behind, then a driver trending low, then mix. One
 * sentence. Title is Needs attention when something is off. The Overview
 * tile rotates attentionAlarms with utilization and cross-report lines.
 *
 * @param {object} input
 * @returns {{ title: string|null, sentence: string|null }}
 */
export function attentionCard(input = {}) {
  const {
    lateRate,
    usualLateRate,
    latePosition,
    volumePosition,
    mixOff,
    pace,
    driverLow
  } = input;
  const visible = attentionAlarms(input);
  const raw = attentionAlarms({
    lateRate,
    usualLateRate,
    latePosition,
    lateCount: input.lateCount,
    lateSample: input.lateSample,
    volumePosition,
    mixOff,
    pace,
    driverLow
  });
  const volumeKnown = volumePosition !== 'unknown' && volumePosition != null;
  const chosen = visible[0] || raw[0] || null;
  if (!volumeKnown) {
    if (!chosen) return { title: null, sentence: null };
    return { title: NEEDS_ATTENTION_TITLE, sentence: chosen.sentence };
  }
  if (!chosen) {
    return { title: ON_PACE_TITLE, sentence: ON_PACE_SENTENCE };
  }
  return { title: NEEDS_ATTENTION_TITLE, sentence: chosen.sentence };
}

/**
 * @param {object} input
 * @returns {string|null}
 */
export function decisionSentence(input) {
  return attentionCard(input).sentence;
}

/**
 * @param {Array<object>} windowJobs
 * @param {Array<object>} usualJobs
 * @returns {{ title: string, label: string, detail: string }}
 */
export function accountShareStat(windowJobs, usualJobs) {
  const todayCounts = countByJobField(windowJobs, 'accountName');
  const usualCounts = countByJobField(usualJobs, 'accountName');
  const top = topCount(todayCounts);
  if (!top) {
    return { title: 'Account share', label: '—', detail: 'No tows in this window' };
  }
  const today = shareOfCounts(top.key, todayCounts);
  const usual = shareOfCounts(top.key, usualCounts);
  return {
    title: 'Account share',
    label: top.key,
    name: top.key,
    factType: 'topAccount',
    detail: `${top.key} is ${today.count} of ${today.total}, ${Math.round(today.share * 100)}%. Usual is ${Math.round(usual.share * 100)}%.`
  };
}

/**
 * Late first, then volume. All names the company. Company names the account.
 * Late branch counts arrivals toward the minimum. Volume branch counts
 * calls. Rank is extra late tows (late minus usual rate times arrivals).
 * Usual-only does not take a slot.
 *
 * @param {object} input
 * @returns {{ title: string, label: string, detail: string, factType: string, name: string }|null}
 */
export function whoIsOffStat({
  groups,
  metric,
  policeMinutes,
  regularMinutes,
  accountType,
  now,
  timeZone,
  minCalls
} = {}) {
  void metric;
  const floor = parseMinCallCount(minCalls);
  let lateOff = null;
  for (const group of groups || []) {
    const today = lateRateByType(
      jobsForResponse(group.jobs).jobs,
      policeMinutes,
      regularMinutes,
      RESPONSE_ON_SCENE,
      accountType
    );
    if (today.sample < floor) continue;
    const usual = lateRateByType(
      jobsForResponse(group.usualJobs).jobs,
      policeMinutes,
      regularMinutes,
      RESPONSE_ON_SCENE,
      accountType
    );
    if (today.rate == null || usual.rate == null) continue;
    const todayPct = today.rate * 100;
    const usualPct = usual.rate * 100;
    const lateBand = paceBand([usualPct]);
    if (pacePosition(todayPct, { ...lateBand, low: usualPct, high: usualPct }) !== 'ahead'
      && todayPct <= usualPct) {
      continue;
    }
    if (todayPct <= usualPct) continue;
    const extraLate = today.late - usual.rate * today.sample;
    if (!lateOff || extraLate > lateOff.extraLate) {
      lateOff = {
        name: group.name,
        todayPct,
        usualPct,
        late: today.late,
        sample: today.sample,
        extraLate
      };
    }
  }
  if (lateOff) {
    return {
      title: 'Off pace',
      label: lateOff.name,
      name: lateOff.name,
      factType: 'offPace',
      detail: `${lateOff.name} is ${Math.round(lateOff.todayPct)}% late (${lateOff.late} of ${lateOff.sample}), against ${Math.round(lateOff.usualPct)}%.`
    };
  }
  let volumeOff = null;
  for (const group of groups || []) {
    if ((group.jobs || []).length < floor) continue;
    const pace = buildPace({
      jobs: group.paceJobs || group.jobs,
      filter: 'day',
      now,
      timeZone
    });
    if (pace.position !== 'behind') continue;
    if (!volumeOff || pace.current < volumeOff.current) {
      volumeOff = { name: group.name, current: pace.current, usual: pace.usual };
    }
  }
  if (volumeOff) {
    return {
      title: 'Off pace',
      label: volumeOff.name,
      name: volumeOff.name,
      factType: 'offPace',
      detail: `${volumeOff.name} is ${formatVolume(volumeOff.current)} tows, against ${formatVolume(Math.round(Number(volumeOff.usual) || 0))} usual.`
    };
  }
  return null;
}

/**
 * @param {object} input
 * @returns {{ title: string, label: string, detail: string, factType?: string, name?: string }|null}
 */
export function offPaceStat(input) {
  return whoIsOffStat(input);
}

/**
 * Vehicle mix tile. Usual-only mix does not take a slot.
 *
 * @param {object} input
 * @returns {{ title: string, label: string, detail: string, factType: string, name: string }|null}
 */
export function vehicleMixStat({ todayJobs, usualJobs } = {}) {
  const off = mixOffFact(todayJobs, usualJobs);
  if (!off) return null;
  return {
    title: 'Vehicle mix',
    label: off.key,
    name: off.key,
    factType: 'classMix',
    detail: off.sentence
  };
}

/**
 * @param {object} input
 * @returns {{ title: string, label: string, detail: string }|null}
 */
export function mixStat(input) {
  return vehicleMixStat(input);
}

/**
 * Slowest 10%. Only when worse than usual. Not a second average.
 *
 * @param {object} input
 * @returns {{ title: string, label: string, detail: string, factType: string }|null}
 */
export function slowestTowsStat({
  todayJobs,
  usualJobs,
  metric
} = {}) {
  const todayDurations = jobsForResponse(todayJobs).jobs
    .map((job) => jobDurationSeconds(job, metric))
    .filter((value) => value != null);
  const usualDurations = jobsForResponse(usualJobs).jobs
    .map((job) => jobDurationSeconds(job, metric))
    .filter((value) => value != null);
  const p90 = percentile(todayDurations, 90);
  const usualP90 = percentile(usualDurations, 90);
  if (p90 == null || usualP90 == null || p90 <= usualP90) return null;
  return {
    title: 'Slowest tows',
    label: formatSeconds(p90),
    factType: 'slowestTows',
    detail: `Slowest 10% took ${formatMinutesShort(p90)}. Usual is ${formatMinutesShort(usualP90)}.`
  };
}

/**
 * @param {object} input
 * @returns {{ title: string, label: string, detail: string }|null}
 */
export function p90Stat(input) {
  return slowestTowsStat(input);
}

/**
 * @param {Array<object>} jobs
 * @returns {{ title: string, label: string, detail: string, factType: string, name: string }|null}
 */
export function busiestDriverStat({ jobs } = {}) {
  const counts = countByJobField(jobs, 'driver');
  const ranked = [...counts.entries()].sort((a, b) => (
    b[1] - a[1] || String(a[0]).localeCompare(String(b[0]))
  ));
  if (ranked.length === 0) return null;
  let total = 0;
  for (const count of counts.values()) total += count;
  const [name, count] = ranked[0];
  const nextCount = ranked[1] ? ranked[1][1] : 0;
  return {
    title: 'Busiest driver',
    label: name,
    name,
    factType: 'topDriver',
    detail: `${name} has ${count} of ${total}. Next has ${nextCount}.`
  };
}

/**
 * Reason heavier than usual in this window. Usual is this window's count
 * at the usual share, not a one-day average against a multi-day count.
 * Usual-only does not take a slot.
 *
 * @param {object} input
 * @returns {{ title: string, label: string, detail: string, factType: string, name: string }|null}
 */
export function busiestReasonStat({ todayJobs, usualJobs } = {}) {
  const todayCounts = countByJobField(todayJobs, 'reason');
  const usualCounts = countByJobField(usualJobs, 'reason');
  const top = topCount(todayCounts);
  if (!top) return null;
  let todayTotal = 0;
  for (const count of todayCounts.values()) todayTotal += count;
  let usualTotal = 0;
  for (const count of usualCounts.values()) usualTotal += count;
  const usualCount = usualCounts.get(top.key) || 0;
  const todayShare = todayTotal ? top.count / todayTotal : 0;
  const usualShare = usualTotal ? usualCount / usualTotal : 0;
  const shareBand = paceBand([usualShare * 100]);
  const todaySharePct = todayShare * 100;
  const shareOff = pacePosition(todaySharePct, {
    usual: shareBand.usual,
    low: usualShare * 100,
    high: usualShare * 100,
    width: 0,
    spread: 0
  }) !== 'on' && todaySharePct !== usualShare * 100;
  const usualInWindow = usualShare * todayTotal;
  if (!shareOff) return null;
  if (top.count <= usualInWindow) return null;
  return {
    title: 'Busiest reason',
    label: top.key,
    name: top.key,
    factType: 'reason',
    detail: `${top.key} is ${top.count} of ${todayTotal}. Usual is ${formatVolume(Math.round(usualInWindow))}.`
  };
}

